import { useCallback, useMemo, useState, useEffect, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/error-boundary";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ArrowRight, Check, ChevronDown, LockKeyhole, Send, Loader2, AlertCircle, Database, RefreshCw } from "lucide-react";
import { Link, Route, Switch, useLocation, useSearch } from "wouter";
import NotFound from "@/pages/not-found";
import BudgetPreviewPage from "@/pages/budget-preview";
import Variance from "@/pages/variance";
import ExplainPage from "@/pages/explain";
import Forecast from "@/pages/forecast";
import ScenariosPage from "@/pages/scenarios";
import OutlookReviewPage from "@/pages/review";
import { Shell, PageHead, Tag, formatMoney, formatVariance, formatPercent, formatPeriod } from "@/components/tekledger-ui";
import { CompanyPicker } from "@/components/company-picker";
import { WorkspaceCompanyProvider, useWorkspaceCompany } from "@/hooks/use-workspace-company";
import { useGetAvailableFiscalYears, useGetFiscalYear, useGetBvaComparison, useGetCurrentForecast, getGetFiscalYearQueryKey, getGetCurrentForecastQueryKey, getGetBvaComparisonQueryKey, getGetAvailableFiscalYearsQueryKey } from "@workspace/api-client-react";
import type { BudgetSource, ActualsSource, BvaComparison, ForecastOutlook } from "@workspace/api-client-react";

const queryClient = new QueryClient();
const LAST_VALID_FISCAL_YEAR_KEY = "tekledger:last-valid-fiscal-year";
const years = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const horizon = ["Aug 26","Sep 26","Oct 26","Nov 26","Dec 26","Jan 27","Feb 27","Mar 27","Apr 27","May 27","Jun 27","Jul 27"];
function Kpis({ bva, isAll, currentForecast }: { bva?: BvaComparison, isAll?: boolean, currentForecast?: ForecastOutlook }) {
  if (!bva) return <LegacyKpis />;
  const { kpis, basis } = bva;
  
  const items = [
    ["FY Budget", formatMoney(kpis.fyBudget), basis.budgetLabel, ""],
    ["YTD Budget", formatMoney(kpis.ytdBudget), `Through ${formatPeriod(`${bva.fiscalYear}-${bva.scope.throughPeriod}`)}`, ""],
    ["YTD Actual", isAll ? "—" : formatMoney(kpis.ytdActual), isAll ? "Select a department" : `Through ${formatPeriod(`${bva.fiscalYear}-${bva.scope.throughPeriod}`)}`, ""],
    [
      "YTD Variance",
      isAll ? "—" : formatVariance(kpis.ytdVariance),
      isAll ? "Select a department" : formatPercent(kpis.ytdVariancePercent),
      isAll ? "" : (kpis.ytdVariance != null && kpis.ytdVariance > 0 ? "bad" : kpis.ytdVariance != null && kpis.ytdVariance < 0 ? "good" : "")
    ]
  ];

  if (currentForecast) {
    items.push(["FY Forecast", formatMoney(currentForecast.currentForecast), "Authoritative Current Outlook", ""]);
    items.push([
      "Forecast vs Budget", 
      formatVariance(currentForecast.forecastVsBudget), 
      "Variance at FY End", 
      currentForecast.forecastVsBudget > 0 ? "bad" : currentForecast.forecastVsBudget < 0 ? "good" : ""
    ]);
  }
  
  return (
    <div className={`kpis ${currentForecast ? "forecast-active" : ""}`}>
      {items.map((x, i) => (
        <div className="kpi" key={x[0]} data-testid={`metric-${i}`}>
          <label>{x[0]}</label>
          <strong className={x[3] === "bad" ? "bad-text" : x[3] === "good" ? "good-text" : ""}>{x[1]}</strong>
          <span>{x[2]}</span>
        </div>
      ))}
    </div>
  );
}
function LegacyKpis(){const items=[["FY Budget","—","Loading...",""],["YTD Budget","—","Loading...",""],["YTD Actual","—","Loading...",""],["YTD Variance","—","Loading...",""]];return <div className="kpis">{items.map((x,i)=><div className="kpi" key={x[0]} data-testid={`metric-${i}`}><label>{x[0]}</label><strong className={x[3]==="bad"?"bad-text":x[3]==="good"?"good-text":""}>{x[1]}</strong><span>{x[2]}</span></div>)}</div>}
function Bars(){return <><div className="bars">{years.map((m,i)=><div className="bar-group" key={m}><i className="bar" style={{height:`${48+i*2}%`}}/><i className="bar forecast" style={{height:`${42+i*3}%`}}/><span className="bar-label">{m}</span></div>)}</div><div className="legend"><span><i className="dot"/>Budget / FY Outlook</span><span><i className="dot forecast"/>Forecast</span></div></>}
function State({ children, active = false }: { children: string; active?: boolean }) {
  return <span className={active ? "tl-fy-state active" : "tl-fy-state"}>{children}</span>;
}

function formatTimestamp(value: string | null | undefined) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function SourceCard({ title, subtitle, source, action }: { title: string; subtitle: string; source?: BudgetSource | ActualsSource; action?: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  if (!source) {
    return (
      <div className="source-card loading" aria-busy="true">
        <div className="source-card-header">
          <div>
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <span className="source-status loading"><Loader2 size={12} className="animate-spin" />Loading</span>
        </div>
        <div className="source-skeleton" />
      </div>
    );
  }

  const isError = source.state === "error";
  const isMissing = source.state === "missing";
  const isOk = source.state === "authoritative";
  const isBudget = "table" in source;
  const stateLabel = !isBudget && isMissing ? "not certified" : source.state;
  const periodValue = isBudget ? source.period : formatPeriod(source.period);
  const timestamps = isBudget
    ? [
        ["Published", formatTimestamp(source.publishedAt)],
        ["Pinned", formatTimestamp(source.pinnedAt)],
      ]
    : [
        ["Certified", formatTimestamp(source.certifiedAt)],
        ["Published", formatTimestamp(source.publishedAt)],
      ];

  return (
    <article className={`source-card ${source.state}`} data-testid={`card-source-${isBudget ? "budget" : "actuals"}`}>
      <div className="source-card-header">
        <button className="source-card-toggle" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} aria-controls={`source-evidence-${isBudget ? "budget" : "actuals"}`}>
          <div>
            <h3>{title}</h3>
            <p>{subtitle}</p>
          </div>
          <ChevronDown size={14} className={expanded ? "expanded" : ""} aria-hidden="true" />
        </button>
        <div className="flex items-center gap-3">
          {action}
          <div className={`source-status ${isOk ? "authoritative" : isError ? "error" : "missing"}`}>
            {isOk && <Check size={12} />}
            {isError && <AlertCircle size={12} />}
            {isMissing && <Database size={12} />}
            {stateLabel}
          </div>
        </div>
      </div>

      {expanded && <div id={`source-evidence-${isBudget ? "budget" : "actuals"}`}>
        {source.message && !isOk && (
          <div className="source-message">{source.message}</div>
        )}

        {!isBudget && (
          <div className="actuals-flow" aria-label="Actuals Explorer certification flow">
            {[
              ["Load", "actuals_source_files → actuals_batches"],
              ["Review", "actuals_rows · validate · reconcile"],
              ["Certify", "actuals_certifications"],
              ["Publish", "actuals_certified_transactions + actuals_authorities"],
            ].map(([step, detail], index) => (
              <div className="actuals-flow-step" key={step}>
                <span>{step}</span>
                <code>{detail}</code>
                {index < 3 && <ArrowRight size={11} aria-hidden="true" />}
              </div>
            ))}
          </div>
        )}

        <div className="source-meta">
          {[
            ["Status", source.status],
            ["Version", source.version ?? "—"],
            ["Period", periodValue],
            ["Rows", source.rowCount.toLocaleString()],
            ["Total", formatMoney(source.total, source.currency)],
            ["Currency", source.currency],
            ...timestamps,
            ["Retrieved", formatTimestamp(source.retrievedAt)],
          ].map(([label, value]) => (
            <div className="source-meta-item" key={label}>
              <span className="source-meta-label">{label}</span>
              <span className="source-meta-value">{value}</span>
            </div>
          ))}
        </div>
        <div className="source-lineage">
          <span>{isBudget ? "Source table" : "Approved contract"}</span>
          <code>{isBudget ? source.table : source.contract}</code>
          <span>Source ID</span>
          <code title={source.sourceId ?? undefined}>{source.sourceId ?? "—"}</code>
        </div>
      </div>}
    </article>
  );
}

function FiscalBvaPanel({
  budgetSource,
  actualsSource,
  fiscalYear,
  companyId,
  fallbackFiscalYear,
  onValidFiscalYear,
  onRestoreFiscalYear,
}: {
  budgetSource?: BudgetSource;
  actualsSource?: ActualsSource;
  fiscalYear: number;
  companyId: number;
  fallbackFiscalYear?: number;
  onValidFiscalYear: (fiscalYear: number) => void;
  onRestoreFiscalYear: (fiscalYear: number) => void;
}) {
  const [showAccounts, setShowAccounts] = useState(false);
  const [coveragePreference, setCoveragePreference] = useState<boolean | null>(null);
  const [showBasis, setShowBasis] = useState(false);
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const urlDepartment = params.get("department");
  const department = urlDepartment ?? "all";
  const { data: bva, isLoading, isError, error, refetch, isFetching } = useGetBvaComparison({
    companyId,
    fiscalYear,
    departmentCode: department,
  }, {
    query: {
      enabled: companyId > 0,
      queryKey: getGetBvaComparisonQueryKey({ companyId, fiscalYear, departmentCode: department }),
    },
  });

  const { data: currentForecast } = useGetCurrentForecast({
    companyId,
    fiscalYear,
    departmentCode: department,
  }, { query: { 
      enabled: department !== "all" && companyId > 0, 
      queryKey: getGetCurrentForecastQueryKey({ companyId, fiscalYear, departmentCode: department }) 
  } });

  useEffect(() => {
    if (!urlDepartment && bva) {
      const readyDepts = bva.coverage.departments.filter(d => d.bvaStatus === "ready");
      if (readyDepts.length === 1) {
        const next = new URLSearchParams(searchString);
        next.set("department", readyDepts[0].departmentCode);
        setLocation(`/fiscal-year?${next.toString()}`, { replace: true });
      }
    }
  }, [urlDepartment, bva, searchString, setLocation]);

  useEffect(() => {
    if (bva) onValidFiscalYear(fiscalYear);
  }, [bva, fiscalYear, onValidFiscalYear]);

  useEffect(() => {
    if (isError && fallbackFiscalYear && fallbackFiscalYear !== fiscalYear) {
      onRestoreFiscalYear(fallbackFiscalYear);
    }
  }, [fallbackFiscalYear, fiscalYear, isError, onRestoreFiscalYear]);

  const changeDepartment = (value: string) => {
    const next = new URLSearchParams(searchString);
    next.set("department", value);
    setShowAccounts(false);
    setCoveragePreference(null);
    setLocation(`/fiscal-year?${next.toString()}`);
  };
  const isRestoringFiscalYear =
    isError && fallbackFiscalYear && fallbackFiscalYear !== fiscalYear;
  if ((isLoading && !bva) || isRestoringFiscalYear) {
    return <div className="bva-loading"><Loader2 className="animate-spin" size={18}/>{isRestoringFiscalYear ? "Restoring previous fiscal year…" : "Loading comparison scope…"}</div>;
  }
  if (isError || !bva) {
    return <div className="source-page-error" role="alert"><AlertCircle size={15}/><div><b>BvA scope could not be checked.</b><span>{error?.message ?? "Comparison data is unavailable."}</span></div><button className="tl-btn" onClick={() => refetch()} disabled={isFetching}>Retry</button></div>;
  }
  const budgetDepartments = bva.coverage.departments.filter((item) => item.budgetStatus === "published");
  const suggestedDepartment = budgetDepartments.find((item) => item.bvaStatus === "ready")
    ?? budgetDepartments.find((item) => item.actualsStatus === "certified");
  const selectedCoverage = department === "all" ? null : budgetDepartments.find((item) => item.departmentCode === department);
  const periodLabel = formatPeriod(`${bva.fiscalYear}-${bva.scope.throughPeriod}`);
  const accountQualityLabel = bva.accountCoverage.status === "complete"
    ? "Complete"
    : bva.accountCoverage.status === "partial"
      ? "Partial"
      : bva.accountCoverage.status === "unmapped"
        ? "Unmapped"
        : "Not Available";
  const isAll = department === "all";
  const actualsCoverageReady = isAll
    ? bva.coverage.actualsDepartmentCount === bva.coverage.budgetDepartmentCount
    : selectedCoverage?.actualsStatus === "certified";
  const throughPeriodNumber = Number(bva.scope.throughPeriod);
  const certifiedPeriods = actualsSource?.periods.filter((item) => Number(item.period.slice(-2)) <= throughPeriodNumber) ?? [];
  const certifiedPeriodRange = certifiedPeriods.length > 0
    ? `${certifiedPeriods[0]?.label} – ${certifiedPeriods.at(-1)?.label}`
    : `Through ${periodLabel}`;
  const certifiedRecordCount = bva.basis.actualsCertificationIds.length;
  const hasCertifiedActuals = certifiedRecordCount > 0;
  const budgetBasisLabel = budgetSource?.status === "pinned"
    ? "Active published Budget"
    : "Published Budget";
  const showCoverage = coveragePreference ?? isAll;
  const openVariance = (period: string) => {
    const next = new URLSearchParams();
    next.set("companyId", String(companyId));
    next.set("fy", String(bva.fiscalYear));
    next.set("department", department);
    next.set("period", period.slice(-2));
    setLocation(`/variance?${next.toString()}`);
  };

  return (
    <div className="fy-bva-overview">
      <section className="bva-scope-bar" data-testid="bva-comparison-scope">
        <div className="bva-scope-main">
          <div className="bva-scope-selections">
            <strong className="bva-scope-title">BvA Basis</strong>
            <label><span>FY</span><b>FY{bva.fiscalYear}</b></label>
            <label><span>{hasCertifiedActuals ? "Through" : "Budget period"}</span><b>{hasCertifiedActuals ? periodLabel : "Full fiscal year"}</b></label>
            <label>
              <span>Scope</span>
              <select value={department} onChange={(event) => changeDepartment(event.target.value)} data-testid="select-comparison-department">
                <option value="all">All Departments</option>
                {budgetDepartments.map((item) => <option key={item.departmentCode} value={item.departmentCode}>{item.departmentCode} · {item.departmentName}</option>)}
              </select>
            </label>
          </div>
          <div className="bva-scope-sources">
            <span><Check size={12} />Budget · {budgetBasisLabel}</span>
            <span>{hasCertifiedActuals ? <Check size={12} /> : <AlertCircle size={12} />}Actuals · {hasCertifiedActuals ? `Certified through ${periodLabel}` : "No certified Actuals for this company"}</span>
          </div>
        </div>
        <div className="bva-scope-actions">
          <button className="tl-btn bva-view-basis" onClick={() => setShowBasis((value) => !value)} aria-expanded={showBasis} data-testid="button-view-basis">
            {showBasis ? "Hide Basis" : "View Basis"}
          </button>
          <div className="bva-scope-status">
            {!isAll && bva.accountCoverage.status !== "not_available" && (
              <button
                className="bva-scope-account"
                onClick={() => setShowAccounts(true)}
                data-testid="button-scope-account-coverage"
              >
                GL coverage {accountQualityLabel.toLowerCase()} · {bva.accountCoverage.matchedAccountCount} matched
              </button>
            )}
            <Tag tone={bva.scope.canCalculate ? "good" : bva.scope.status === "partial_coverage" ? "bad" : ""}>
              {bva.scope.canCalculate ? "✓ Ready" : bva.scope.status === "partial_coverage" ? "Partial coverage" : "Not ready"}
            </Tag>
          </div>
        </div>
      </section>
      {showBasis && (
        <section className="bva-basis-detail" data-testid="basis-detail">
          <div className="bva-basis-friendly">
            <span>Budget authority</span>
            <b><Check size={13} />{budgetBasisLabel}</b>
            <p>{bva.basis.budgetLabel}</p>
            <small>{budgetSource?.version ? `Version ${budgetSource.version}` : "Current approved plan"}</small>
          </div>
          <div className="bva-basis-friendly">
            <span>Actuals authority</span>
            <b>{hasCertifiedActuals ? <Check size={13} /> : <AlertCircle size={13} />}{hasCertifiedActuals ? "Certified Actuals" : "No certified Actuals"}</b>
            <p>{hasCertifiedActuals ? certifiedPeriodRange : bva.basis.actualsLabel}</p>
            <small>{certifiedPeriods.length || certifiedRecordCount} monthly snapshot{(certifiedPeriods.length || certifiedRecordCount) === 1 ? "" : "s"} · immutable transactions</small>
          </div>
          <div className="bva-basis-friendly">
            <span>Certification records</span>
            <b><Check size={13} />{certifiedRecordCount} monthly certification{certifiedRecordCount === 1 ? "" : "s"}</b>
            <p>{certifiedRecordCount > 0 ? `All available through ${periodLabel}` : "No certified records available"}</p>
            <small>{actualsSource?.certifiedAt ? `Latest certified ${formatTimestamp(actualsSource.certifiedAt)}` : "Certification date unavailable"}</small>
          </div>
        </section>
      )}

      {isAll && bva.scope.status === "partial_coverage" && (
        <section className={`bva-coverage-alert blocked`}>
          <div><div className="tl-fy-kicker">Partial Actuals coverage</div><h3>{bva.scope.message}</h3></div>
          <div className="bva-alert-counts">
            <span>Budget coverage<b>{bva.coverage.budgetDepartmentCount} departments</b></span>
            <span>Certified Actuals coverage<b>{bva.coverage.actualsDepartmentCount} departments</b></span>
            <span>Comparable departments<b>{bva.coverage.matchedDepartmentCount} of {bva.coverage.budgetDepartmentCount}</b></span>
          </div>
          <div className="bva-alert-actions">
            {suggestedDepartment && <button className="tl-btn primary" onClick={() => changeDepartment(suggestedDepartment.departmentCode)} data-testid="button-compare-department">Compare Department {suggestedDepartment.departmentCode}</button>}
            <button className="tl-btn" onClick={() => setCoveragePreference(true)} data-testid="button-view-coverage">View Coverage</button>
          </div>
        </section>
      )}

      <Kpis bva={bva} isAll={isAll} currentForecast={currentForecast} />

      <section className={`tl-fy-close bva-current-close ${bva.scope.canCalculate ? "" : "blocked"}`}>
        <div>
          <div className="tl-fy-kicker">Current close</div>
          <h2>{hasCertifiedActuals ? `${periodLabel} Close` : "Awaiting certified Actuals"}</h2>
          <p>{bva.scope.canCalculate && bva.accountCoverage.status === "partial" ? "Department-level BvA is ready; GL account matching is partial." : bva.scope.message}</p>
        </div>
        <div className="tl-fy-steps">
          <div className="tl-fy-step"><label>Budget</label><b>✓ Published</b></div>
          <div className="tl-fy-step"><label>Actuals</label><b>{!hasCertifiedActuals ? "Missing" : actualsCoverageReady ? "✓ Certified" : "Partial"}</b></div>
          <div className="tl-fy-step"><label>BvA</label><b>{bva.scope.canCalculate ? "✓ Ready" : bva.scope.status === "partial_coverage" ? "Partial" : "Not ready"}</b></div>
          {!isAll && <div className="tl-fy-step"><label>GL Match</label><b>{accountQualityLabel}</b></div>}
        </div>
        <div className="bva-close-action">
          {isAll && !bva.scope.canCalculate && suggestedDepartment ? (
            <button className="tl-btn primary" onClick={() => changeDepartment(suggestedDepartment.departmentCode)} data-testid="button-close-compare-department">
              Compare Department {suggestedDepartment.departmentCode}
            </button>
          ) : (
            <button className="tl-btn primary" onClick={() => openVariance(bva.scope.throughPeriod)} disabled={!bva.scope.canCalculate} data-testid="button-close-analyze-variance">
              Analyze {periodLabel} Variance <ArrowRight size={13} />
            </button>
          )}
        </div>
      </section>

      {!isAll && (
        <section className="tl-fy-tablecard" data-testid="monthly-bva-workbench">
          <div className="tl-fy-tabletitle">
            <div>
              <div className="tl-fy-kicker">Primary workbench</div>
              <h3>Monthly Budget vs Actual</h3>
              <p>Budget comes from published monthly spreads; Actuals come only from certified transactions in the selected scope.</p>
            </div>
            <span className="tl-mini">FY{bva.fiscalYear} · Through {periodLabel}</span>
          </div>
          <div className="table-wrap">
            <table className="tl-fy-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Budget</th>
                  <th>Actual</th>
                  <th>Variance</th>
                  <th>Var %</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {bva.monthly.map((row) => {
                  const isCurrentClose = hasCertifiedActuals && row.period.endsWith(`-${bva.scope.throughPeriod}`);
                  const actionLabel = row.status !== "ready" ? "Unavailable" : "Analyze";
                  return (
                    <tr key={row.period} className={isCurrentClose ? "current" : ""}>
                      <td>{row.label}{isCurrentClose && <span className="bva-current-label">Current close</span>}</td>
                      <td>{row.budget === null ? "Not Available" : formatMoney(row.budget)}</td>
                      <td>{row.actual === null ? "Not Available" : formatMoney(row.actual)}</td>
                      <td className={row.variance !== null && row.variance > 0 ? "bad-text" : row.variance !== null && row.variance < 0 ? "good-text" : ""}>
                        {row.variance === null ? "Not Calculated" : formatVariance(row.variance)}
                      </td>
                      <td>{row.variancePercent === null ? "—" : formatPercent(row.variancePercent)}</td>
                      <td>
                        <button
                          className={`tl-fy-action ${isCurrentClose && row.status === "ready" ? "primary" : ""}`}
                          disabled={row.status !== "ready"}
                          onClick={() => openVariance(row.period)}
                          data-testid={`button-analyze-period-${row.period}`}
                        >
                          {actionLabel}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card bva-coverage-card">
        <div className="card-head">
          <div>
            <div className="tl-fy-kicker">Comparison coverage</div>
            <h3>{bva.coverage.matchedDepartmentCount} of {bva.coverage.budgetDepartmentCount} departments ready for BvA</h3>
          </div>
          <div className="flex items-center gap-3">
            <Tag tone={bva.coverage.matchedDepartmentCount === bva.coverage.budgetDepartmentCount ? "good" : "bad"}>
              {bva.coverage.matchedDepartmentCount === bva.coverage.budgetDepartmentCount ? "Complete" : "Partial"}
            </Tag>
            <button
              className="tl-btn"
              onClick={() => setCoveragePreference(!showCoverage)}
              aria-expanded={showCoverage}
              data-testid="button-toggle-coverage"
            >
              {showCoverage ? "Hide coverage" : "Show coverage"}
            </button>
          </div>
        </div>
        {showCoverage && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Department</th><th>Budget Coverage</th><th>Actual Period Coverage</th><th>Dept BvA</th><th>GL Match</th><th>Action</th></tr>
              </thead>
              <tbody>
                {budgetDepartments.map((item) => (
                  <tr key={item.departmentCode} onClick={() => changeDepartment(item.departmentCode)} className={department === item.departmentCode ? "selected" : ""} data-testid={`coverage-department-${item.departmentCode}`}>
                    <td><b>{item.departmentCode} {item.departmentName}</b></td>
                    <td><Tag tone="good">Complete</Tag></td>
                    <td><Tag tone={item.actualsStatus === "certified" ? "good" : "bad"}>{item.actualsStatus === "certified" ? `P01–P${String(item.requiredPeriodCount).padStart(2, "0")} Complete` : item.coveredActualPeriods.length ? `${item.coveredActualPeriods.join(", ")} only` : "Missing"}</Tag></td>
                    <td><Tag tone={item.bvaStatus === "ready" ? "good" : "bad"}>{item.bvaStatus === "ready" ? "Ready" : item.actualsStatus === "partial" ? "Partial Period Coverage" : "Not Ready"}</Tag></td>
                    <td>{item.accountStatus === "complete" ? <Tag tone="good">Complete</Tag> : item.accountStatus === "partial" ? <Tag tone="bad">Partial · {item.matchedAccountCount} matched</Tag> : "Not Evaluated"}</td>
                    <td><button className="tl-fy-action" onClick={(event) => { event.stopPropagation(); changeDepartment(item.departmentCode); }}>{item.bvaStatus === "ready" ? "Review" : "View Coverage"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {!isAll && (
        <section className="card bva-account-card" data-testid="gl-account-coverage">
          <div className="card-head">
            <div><div className="tl-fy-kicker">GL account coverage</div><h3>{accountQualityLabel}</h3><p>{bva.accountCoverage.message}</p></div>
            <div className="bva-account-actions"><Tag tone={bva.accountCoverage.status === "complete" ? "good" : bva.accountCoverage.status === "not_available" ? "" : "bad"}>{accountQualityLabel}</Tag>{bva.accountCoverage.accounts.length > 0 && <button className="tl-btn" onClick={() => setShowAccounts((value) => !value)} data-testid="button-review-unmatched-accounts">{showAccounts ? "Hide account detail" : "Review unmatched accounts"}</button>}</div>
          </div>
          {showAccounts && bva.accountCoverage.status !== "not_available" && <>
            <div className="bva-account-summary">
              <span>Budget GL accounts<b>{bva.accountCoverage.budgetAccountCount}</b></span>
              <span>Actual GL accounts<b>{bva.accountCoverage.actualsAccountCount}</b></span>
              <span>Matched<b>{bva.accountCoverage.matchedAccountCount}</b></span>
              <span>Budget-only<b>{bva.accountCoverage.budgetOnlyCount}</b></span>
              <span>Actual-only<b>{bva.accountCoverage.actualOnlyCount}</b></span>
            </div>
            <div className="bva-reconciliation">
              <span><Check size={13}/> Budget accounts {bva.accountCoverage.budgetReconciled ? "reconcile" : "do not reconcile"} to {formatMoney(bva.accountCoverage.departmentBudget)}</span>
              <span><Check size={13}/> Actual accounts {bva.accountCoverage.actualReconciled ? "reconcile" : "do not reconcile"} to {formatMoney(bva.accountCoverage.departmentActual)}</span>
            </div>
          </>}
          {showAccounts && bva.accountCoverage.accounts.length > 0 && <div className="table-wrap bva-account-table"><table><thead><tr><th>GL Account</th><th>Account Name</th><th>Budget</th><th>Actual</th><th>Variance</th><th>Status</th></tr></thead><tbody>{bva.accountCoverage.accounts.map((account) => <tr key={`${account.glAccount}-${account.status}`}><td><b>{account.glAccount || "Unmapped"}</b></td><td>{account.accountName ?? "—"}</td><td>{formatMoney(account.budget)}</td><td>{formatMoney(account.actual)}</td><td className={account.variance > 0 ? "bad-text" : account.variance < 0 ? "good-text" : ""}>{formatVariance(account.variance)}</td><td><Tag tone={account.status === "matched" ? "good" : "bad"}>{account.status === "matched" ? "Matched" : account.status === "budget_only" ? "Budget Only" : account.status === "actual_only" ? "Actual Only" : "Mapping Required"}</Tag></td></tr>)}</tbody></table></div>}
        </section>
      )}

      <section className="bva-source-controls" data-testid="source-controls">
        <div className="bva-supporting-kicker">Supporting controls</div>
        <SourceCard
          title="Budget Source"
          subtitle="Published Budget Explorer authority"
          source={budgetSource}
        />
        <SourceCard
          title="Actuals Source"
          subtitle="Certified Actuals authority"
          source={actualsSource}
        />
      </section>
    </div>
  );
}

function FiscalYear() {
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const { companyId, companyName, isLoadingCompanies } = useWorkspaceCompany();
  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const requestedFiscalYear = Number(params.get("fy"));
  const [lastValidFiscalYear, setLastValidFiscalYear] = useState<number | null>(() => {
    const stored = Number(window.localStorage.getItem(LAST_VALID_FISCAL_YEAR_KEY));
    return Number.isInteger(stored) && stored >= 2000 && stored <= 2100
      ? stored
      : null;
  });
  const {
    data: availableFiscalYears,
    isLoading: isLoadingFiscalYears,
    isError: isFiscalYearDiscoveryError,
    refetch: retryFiscalYearDiscovery,
    isFetching: isRetryingFiscalYearDiscovery,
  } = useGetAvailableFiscalYears(
    { companyId },
    {
      query: {
        enabled: companyId > 0,
        queryKey: getGetAvailableFiscalYearsQueryKey({ companyId }),
      },
    },
  );
  const availableYearValues = availableFiscalYears?.fiscalYears ?? [];
  const storedFiscalYear = lastValidFiscalYear
    && availableYearValues.includes(lastValidFiscalYear)
    ? lastValidFiscalYear
    : null;
  const defaultFiscalYear = storedFiscalYear
    || availableFiscalYears?.defaultFiscalYear
    || lastValidFiscalYear
    || 2026;
  const requestedFiscalYearIsValid = Number.isInteger(requestedFiscalYear)
    && requestedFiscalYear >= 2000
    && requestedFiscalYear <= 2100
    && (!availableFiscalYears || availableYearValues.includes(requestedFiscalYear));
  const fiscalYear = requestedFiscalYearIsValid
    ? requestedFiscalYear
    : defaultFiscalYear;
  const fiscalYearOptions = availableFiscalYears?.fiscalYears.length
    ? [...availableFiscalYears.fiscalYears].sort((a, b) => b - a)
    : [fiscalYear];

  useEffect(() => {
    if (!availableFiscalYears) return;
    const requestedIsAvailable = availableFiscalYears.fiscalYears.includes(requestedFiscalYear);
    if (requestedIsAvailable || availableFiscalYears.defaultFiscalYear === 0) return;
    const next = new URLSearchParams(searchString);
    next.set("fy", String(availableFiscalYears.defaultFiscalYear));
    next.delete("department");
    setLocation(`/fiscal-year?${next.toString()}`, { replace: true });
  }, [availableFiscalYears, requestedFiscalYear, searchString, setLocation]);

  const { data: fy, isLoading } = useGetFiscalYear(
    { companyId, fiscalYear },
    {
      query: {
        enabled: Boolean(availableFiscalYears) && companyId > 0,
        queryKey: getGetFiscalYearQueryKey({ companyId, fiscalYear }),
      },
    },
  );
  const certificationLabel = fy?.actuals.certifiedAt
    ? `Certified ${formatTimestamp(fy.actuals.certifiedAt)}`
    : isLoading ? "Checking certified Actuals…" : "No certified Actuals";
  const changeFiscalYear = (value: string) => {
    const nextFiscalYear = Number(value);
    if (!availableFiscalYears?.fiscalYears.includes(nextFiscalYear)) return;
    const next = new URLSearchParams(searchString);
    next.set("companyId", String(companyId));
    next.set("fy", String(nextFiscalYear));
    next.delete("department");
    setLocation(`/fiscal-year?${next.toString()}`);
  };
  const rememberValidFiscalYear = useCallback((validFiscalYear: number) => {
    window.localStorage.setItem(LAST_VALID_FISCAL_YEAR_KEY, String(validFiscalYear));
    setLastValidFiscalYear(validFiscalYear);
  }, []);
  const restoreFiscalYear = useCallback((restoredFiscalYear: number) => {
    const next = new URLSearchParams(searchString);
    next.set("fy", String(restoredFiscalYear));
    next.delete("department");
    setLocation(`/fiscal-year?${next.toString()}`, { replace: true });
  }, [searchString, setLocation]);
  const fallbackFiscalYear = storedFiscalYear && storedFiscalYear !== fiscalYear
    ? storedFiscalYear
    : fiscalYearOptions.find((year) => year < fiscalYear)
      ?? fiscalYearOptions.find((year) => year !== fiscalYear);

  return (
    <Shell active="Fiscal Year" certificationLabel={certificationLabel}>
      <section className="tl-content">
        <div className="tl-fy-head">
          <div>
            <div className="tl-eyebrow">Annual operating control</div>
            <h1 className="tl-title">FY{fiscalYear} Financial Performance</h1>
            <p className="tl-sub">
              BvA for {companyName} · FY{fiscalYear}. Track monthly actuals against the approved budget and manage the current full-year outlook.
            </p>
          </div>
          <div className="tl-fy-pickers">
            <CompanyPicker />
            <div className="tl-fy-picker">
            <div className="tl-select-shell">
              <select
                className="tl-select"
                value={fiscalYear}
                onChange={(event) => changeFiscalYear(event.target.value)}
                disabled={isLoadingFiscalYears || isFiscalYearDiscoveryError || availableFiscalYears?.fiscalYears.length === 0}
                aria-label="Fiscal Year"
                data-testid="select-fiscal-year"
              >
                {fiscalYearOptions.map((year) => <option key={year} value={year}>FY{year}</option>)}
              </select>
              <ChevronDown size={14} aria-hidden="true" />
            </div>
            {isFiscalYearDiscoveryError && <span className="tl-fy-picker-error">Year list unavailable</span>}
          </div>
          </div>
        </div>
        {isLoadingCompanies && companyId <= 0 ? (
          <div className="bva-loading"><Loader2 className="animate-spin" size={18}/>Loading workspace companies…</div>
        ) : isFiscalYearDiscoveryError ? (
          <div className="source-page-error" role="alert" data-testid="fiscal-year-discovery-error">
            <AlertCircle size={15}/>
            <div><b>Fiscal years could not be loaded.</b><span>Budget Explorer could not be reached. Retry to load this company's available fiscal years.</span></div>
            <button className="tl-btn" onClick={() => retryFiscalYearDiscovery()} disabled={isRetryingFiscalYearDiscovery}>Retry</button>
          </div>
        ) : availableFiscalYears && companyId > 0 ? (
          <FiscalBvaPanel
            budgetSource={fy?.budget}
            actualsSource={fy?.actuals}
            fiscalYear={fiscalYear}
            companyId={companyId}
            fallbackFiscalYear={fallbackFiscalYear}
            onValidFiscalYear={rememberValidFiscalYear}
            onRestoreFiscalYear={restoreFiscalYear}
          />
        ) : (
          <div className="bva-loading"><Loader2 className="animate-spin" size={18}/>Loading fiscal year…</div>
        )}
      </section>
    </Shell>
  );
}
function PerformanceRedirect() {
  const searchString = useSearch();
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation(searchString ? `/fiscal-year?${searchString}` : "/fiscal-year", { replace: true });
  }, [searchString, setLocation]);

  return <div className="bva-loading">Redirecting to Fiscal Year…</div>;
}

function LegacyBvaPage() {
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const departmentParam = searchParams.get("department") || undefined;

  const { companyId } = useWorkspaceCompany();
  const { data: bva, isLoading, isError, error, refetch, isFetching } = useGetBvaComparison({
    companyId,
    fiscalYear: 2026,
    ...(departmentParam ? { departmentCode: departmentParam } : {})
  });

  const setDepartment = (dept: string | undefined) => {
    const params = new URLSearchParams(searchString);
    if (dept) params.set("department", dept);
    else params.delete("department");
    setLocation(`/fiscal-year?${params.toString()}`);
  };

  return (
    <Shell active="Fiscal Year">
      <section className="tl-content">
        <PageHead eyebrow="Fiscal Year BvA" title="Fiscal Year" sub="Budget vs Actual comparison across authorized departments." />
        
        {isLoading && !bva ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", color: "#69807a" }}>
            <Loader2 className="animate-spin" size={24} style={{ marginBottom: 16 }} />
            <p style={{ fontFamily: "var(--app-font-mono)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>Loading comparison data...</p>
          </div>
        ) : isError ? (
          <div className="source-page-error" role="alert">
            <AlertCircle size={15} />
            <div><b>Could not load BvA comparison.</b><span>{error?.message || "An error occurred"}</span></div>
            <button className="tl-btn" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} />Retry
            </button>
          </div>
        ) : bva ? (
          <>
            <div className="card" style={{ marginBottom: 20, borderLeft: "4px solid #315c54" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <span className="mini" style={{ letterSpacing: "0.12em", textTransform: "uppercase", color: "#5b8076", fontWeight: 700 }}>Comparison Scope</span>
                  <h2 style={{ fontSize: 22, color: "#1e4b4a", margin: "6px 0", fontWeight: 800 }}>
                    {bva.scope.departmentName || "All Departments"}
                  </h2>
                  <p style={{ margin: 0, fontSize: 13, color: "#58736d", maxWidth: 650, lineHeight: 1.6 }}>
                    {bva.scope.message}
                  </p>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                    <Tag tone={bva.scope.status === 'comparison_ready' ? 'good' : bva.scope.status === 'partial_coverage' ? 'bad' : ''}>
                      {bva.scope.status.replaceAll("_", " ")}
                    </Tag>
                    {bva.scope.canCalculate ? (
                      <span style={{ fontSize: 11, fontFamily: "var(--app-font-mono)", color: "#4d7770", display: "flex", alignItems: "center", gap: 4 }}>
                        <Check size={13} style={{ color: "#28776c" }} /> Calculation Ready
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontFamily: "var(--app-font-mono)", color: "#8d4b39", display: "flex", alignItems: "center", gap: 4 }}>
                        <AlertCircle size={13} style={{ color: "#aa5d45" }} /> Calculation Blocked
                      </span>
                    )}
                  </div>
                </div>
                
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 220 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#4d7770", textTransform: "uppercase", letterSpacing: "0.06em" }}>Select Department</label>
                  <select 
                    value={departmentParam || ""}
                    onChange={(e) => setDepartment(e.target.value || undefined)}
                    style={{ background: "#fff", borderColor: "#dce3dc", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontWeight: 600, color: "#1e4b4a" }}
                  >
                    {!departmentParam && bva.scope.departmentCode && (
                      <option value="">Auto: {bva.scope.departmentName}</option>
                    )}
                    <option value="all">All Departments</option>
                    {bva.coverage.departments.map(d => (
                      <option key={d.departmentCode} value={d.departmentCode}>
                        {d.departmentName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="bva-coverage-summary" aria-label="Comparison coverage summary">
                  <div><span>Budget coverage</span><b>{bva.coverage.budgetDepartmentCount} departments</b></div>
                  <div><span>Actuals coverage</span><b>{bva.coverage.actualsDepartmentCount} departments</b></div>
                  <div><span>Comparison ready</span><b>{bva.coverage.matchedDepartmentCount} of {bva.coverage.budgetDepartmentCount}</b></div>
                </div>
              </div>
            </div>

            <Kpis bva={bva} />

            <div className="grid2">
              <div className="card">
                <div className="card-head">
                  <div>
                    <h3>Monthly Variance Detail</h3>
                    <span className="mini">Through {formatPeriod(`${bva.fiscalYear}-${bva.scope.throughPeriod}`)}</span>
                  </div>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Budget</th>
                        <th>Actual</th>
                        <th>Variance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bva.monthly.map((m) => (
                        <tr key={m.period}>
                          <td>{m.label}</td>
                          <td>{formatMoney(m.budget)}</td>
                          <td>{formatMoney(m.actual)}</td>
                          <td className={m.variance != null && m.variance > 0 ? "bad-text" : m.variance != null && m.variance < 0 ? "good-text" : ""}>
                            {formatVariance(m.variance)}
                            {m.variancePercent != null && (
                              <span style={{ fontSize: 10, display: "block", marginTop: 2, color: "#77817b" }}>
                                {formatPercent(m.variancePercent)}
                              </span>
                            )}
                          </td>
                          <td>
                            <Tag tone={m.status === 'ready' ? 'good' : 'bad'}>
                              {m.status.replaceAll("_", " ")}
                            </Tag>
                          </td>
                        </tr>
                      ))}
                      {bva.monthly.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: "center", padding: "30px 10px", color: "#84908b", fontStyle: "italic" }}>
                            No monthly data available for this scope.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div className="card">
                  <div className="card-head">
                    <h3>Source Lineage</h3>
                    <Tag>Audit Evidence</Tag>
                  </div>
                  <div style={{ background: "#f8faf7", border: "1px solid #dfe7e2", borderRadius: 10, padding: 16 }}>
                    <span style={{ display: "block", marginBottom: 4, fontSize: 10, color: "#69807a", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Budget</span>
                    <strong style={{ display: "block", fontSize: 13, color: "#18332f", marginBottom: 4 }}>{bva.basis.budgetLabel}</strong>
                    <code style={{ fontSize: 11, color: "#355c55", wordBreak: "break-all", background: "#fff", padding: "4px 6px", borderRadius: 4, border: "1px solid #e7ece8", display: "block", marginBottom: 16 }}>
                      {bva.basis.budgetSourceId || "No source"}
                    </code>

                    <span style={{ display: "block", marginBottom: 4, fontSize: 10, color: "#69807a", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Actuals</span>
                    <strong style={{ display: "block", fontSize: 13, color: "#18332f", marginBottom: 4 }}>{bva.basis.actualsLabel}</strong>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {bva.basis.actualsSourceIds.map((id, i) => (
                        <code key={i} style={{ fontSize: 11, color: "#355c55", wordBreak: "break-all", background: "#fff", padding: "4px 6px", borderRadius: 4, border: "1px solid #e7ece8", display: "block" }}>
                          {id}
                        </code>
                      ))}
                      {bva.basis.actualsSourceIds.length === 0 && (
                        <span style={{ fontSize: 11, color: "#a3aaa5", fontStyle: "italic" }}>No sources</span>
                      )}
                    </div>
                    
                    <span style={{ display: "block", marginTop: 16, marginBottom: 4, fontSize: 10, color: "#69807a", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Certifications</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {bva.basis.actualsCertificationIds.map((id, i) => (
                        <code key={i} style={{ fontSize: 11, color: "#1e5246", wordBreak: "break-all", background: "#e9f2eb", padding: "4px 6px", borderRadius: 4, border: "1px solid #c2d8cc", display: "block" }}>
                          {id}
                        </code>
                      ))}
                      {bva.basis.actualsCertificationIds.length === 0 && (
                        <span style={{ fontSize: 11, color: "#a3aaa5", fontStyle: "italic" }}>No certifications</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <div>
                      <h3>Department Coverage</h3>
                      <span className="mini">{bva.coverage.matchedDepartmentCount} matched</span>
                    </div>
                  </div>
                  <div className="table-wrap" style={{ maxHeight: 320, overflowY: "auto" }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Department</th>
                          <th style={{ textAlign: "right" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bva.coverage.departments.map(d => {
                           const isReady = d.bvaStatus === 'ready';
                           const isCurrent = (departmentParam === d.departmentCode) || (!departmentParam && bva.scope.departmentCode === d.departmentCode) || (departmentParam === 'all' && bva.scope.departmentCode === null);
                           return (
                             <tr 
                               key={d.departmentCode} 
                               onClick={() => isReady ? setDepartment(d.departmentCode) : undefined}
                               style={{ cursor: isReady ? "pointer" : "default", opacity: isReady ? 1 : 0.6, background: isCurrent && d.departmentCode === departmentParam ? "#e9f3ee" : undefined }}
                             >
                               <td>
                                 <strong style={{ display: "block", fontSize: 12, fontWeight: isCurrent ? 800 : 500, color: "#18332f" }}>{d.departmentName}</strong>
                                  {isReady && d.accountStatus === "partial" && <span style={{ display: "block", fontSize: 10, color: "#9a7041", marginTop: 4, fontFamily: "var(--app-font-mono)" }}>GL coverage partial · {d.matchedAccountCount} matched</span>}
                                  {!isReady && <span style={{ display: "block", fontSize: 10, color: "#aa5d45", marginTop: 4, fontFamily: "var(--app-font-mono)" }}>{d.budgetStatus !== "published" ? "Budget missing" : d.actualsStatus === "partial" ? "Partial period coverage" : "Actuals missing"}</span>}
                               </td>
                               <td style={{ textAlign: "right" }}>
                                 <Tag tone={isReady ? 'good' : 'bad'}>{d.bvaStatus.replace("_", " ")}</Tag>
                               </td>
                             </tr>
                           );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </section>
    </Shell>
  );
}
function Explain(){const [cause,setCause]=useState("Volume / Usage");const [behavior,setBehavior]=useState("Recurring");const [done,setDone]=useState(false);return <Shell active="Explain"><section className="tl-content"><PageHead eyebrow="Explanation queue · July close" title="Explain" sub="Classify the cause once; the resulting forecast adjustment flows directly into Forecast." action="Materiality settings"/><div className="note" style={{marginBottom:16}}>One-Time affects only the current period. Timing moves expense between months. Recurring extends run rate. Structural changes the baseline through an end date.</div>{[["Cloud hosting · Infrastructure","$500K","$650K","+$150K U","+$750K","Elena R."],["Contractor services · Product","$5.6M","$6.5M","+$0.9M","+$2.6M","Dara K."],["EMEA launch events · Marketing","$2.1M","$2.8M","+$0.7M","+$1.1M","Marisol C."]].map((r,i)=><article className="queue-item" key={r[0]}><div className="card-head"><div><h3>{r[0]}</h3><Tag tone="bad">Unfavorable</Tag></div><Tag>{done&&i===2?"Complete":"In progress"}</Tag></div><div className="metrics"><span>Budget <b>{r[1]}</b></span><span>Actual <b>{r[2]}</b></span><span>Variance <b className="bad-text">{r[3]}</b></span><span>FY impact <b>{r[4]}</b></span><span>Rolling impact <b>+$1.8M</b></span></div><p>Usage rose after the product release. Commitments indicate elevated consumption persists through Q3.</p><div className="method-row"><select value={cause} onChange={e=>setCause(e.target.value)} data-testid={`select-cause-${i}`}><option>Volume / Usage</option><option>Price / Rate</option><option>Timing</option><option>Scope change</option></select><select value={behavior} onChange={e=>setBehavior(e.target.value)} data-testid={`select-behavior-${i}`}><option>Recurring</option><option>One-Time</option><option>Timing</option><option>Structural</option></select><button className="method active">FY Outlook: +{r[4]}</button><button className="method">Rolling 12M: +$1.8M</button></div><div className="method-row" style={{marginTop:10}}><span className="mini">Effective From <b>Aug 2026</b></span><span className="mini">Through <b>Jul 2027</b></span><span className="mini">Owner <b>{r[5]}</b></span><span className="mini">Cause <b>{cause}</b> · Behavior <b>{behavior}</b></span></div><div className="note" style={{marginTop:10}}>Saved explanation is now a Forecast input; no duplicate re-entry.</div><div className="method-row" style={{marginTop:10}}><button className="btn" data-testid={`button-add-action-${i}`}>Add action</button>{i===2&&<button className="btn primary" onClick={()=>setDone(true)} data-testid="button-mark-explanation-complete"><Check size={12}/>Mark complete</button>}</div></article>)}</section></Shell>}
function History(){const [selected,setSelected]=useState("Jul");const rows=[["Jan","Jan","$24.3M","+$0.3M U","$25.0M","—"],["Feb","Feb","$24.5M","+$0.5M U","$25.4M","+$0.4M"],["Mar","Mar","$25.1M","+$1.1M U","$26.0M","+$0.6M"],["Jul","Jul","$26.0M","+$2.0M U","$27.1M","+$0.9M"]];return <Shell active="Outlook History"><section className="tl-content"><PageHead eyebrow="Immutable publication record" title="Outlook History" sub="Read both outlook narratives as they were known at each monthly close." action="Export history"/><div className="grid2"><div className="card"><div className="card-head"><h3>Outlook evolution</h3><span className="mini">FY Outlook + Rolling 12M · USD millions</span></div><Bars/></div><div className="card"><div className="card-head"><h3>Snapshot reconstruction</h3><Tag tone="good">{selected} close</Tag></div><div className="snapshot"><strong><LockKeyhole size={12}/> Known state at {selected} 2026 close</strong><span>Certified actuals · forecast months · assumptions · variance explanations · forecast changes · adopted scenarios · management actions</span></div><button className="btn primary" style={{marginTop:12}} data-testid="button-open-reconstruction">Open immutable reconstruction</button></div></div><div className="card"><div className="card-head"><h3>Published outlooks</h3><Tag>4 records</Tag></div><div className="table-wrap"><table><thead><tr><th>Snapshot</th><th>Actual Through</th><th>FY Outlook</th><th>vs Budget</th><th>Rolling 12M</th><th>Change vs Prior</th></tr></thead><tbody>{rows.map(r=><tr key={r[0]} onClick={()=>setSelected(r[0])} style={selected===r[0]?{background:"#edf4ef"}:{}} data-testid={`row-snapshot-${r[0].toLowerCase()}`}><td><b>{r[0]}</b></td><td>{r[1]}</td><td>{r[2]}</td><td className="bad-text">{r[3]}</td><td>{r[4]}</td><td className="bad-text">{r[5]}</td></tr>)}</tbody></table></div><div className="note">Click a snapshot to reconstruct the financial state known at that close. Budget and certified actuals remain immutable; only the future horizon changes.</div></div></section></Shell>}
export function TekLedgerRouter(){return <Switch><Route path="/" component={FiscalYear}/><Route path="/fiscal-year" component={FiscalYear}/><Route path="/fiscal-year/budget-preview" component={BudgetPreviewPage}/><Route path="/performance" component={PerformanceRedirect}/><Route path="/variance" component={Variance}/><Route path="/explain" component={ExplainPage}/><Route path="/forecast" component={Forecast}/><Route path="/scenarios" component={ScenariosPage}/><Route path="/review" component={OutlookReviewPage}/><Route path="/outlook-history" component={History}/><Route component={NotFound}/></Switch>}
function App(){return <QueryClientProvider client={queryClient}><TooltipProvider><ErrorBoundary resetKey={location.pathname}><WorkspaceCompanyProvider><TekLedgerRouter/></WorkspaceCompanyProvider></ErrorBoundary><Toaster/></TooltipProvider></QueryClientProvider>}
export default App;